import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Menu, MenuItem, Button, Grid } from '@mui/material';
import Iconify from '../../../reusable-components/iconify';
import { useDispatch } from '../../../redux/store';
import { toggleTicketsComplete } from '../../../redux/slices/tickets';
import { DateRangePickerSelect } from '../../../reusable-components/date-range-picker';
import { ScopeGuard } from '../../../reusable-components/scopes';
import TicketTabs from './TicketTabs';
import DropdownAction from '../../../reusable-components/dropdown-action/DropdownAction';

CustomToolbar.propTypes = {
  setShowSelection: PropTypes.func,
  showSelection: PropTypes.string,
  selected: PropTypes.array,
  markAllAsRead: PropTypes.func,
};
export default function CustomToolbar({ setShowSelection, showSelection, selected, markAllAsRead = () => {} }) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    data: { activeTickets },
  } = useSelector((state) => state.tickets);
  const status = searchParams.get('status');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (label) => {
    if (label) setShowSelection(label);
    setAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setShowSelection('');
    setAnchorEl(null);
  };

  const toggleComplete = (value) => {
    setShowSelection('');
    setAnchorEl(null);
    dispatch(toggleTicketsComplete({ ticketIds: selected, isComplete: value }));
  };

  const isActive = status !== 'complete';

  return (
    <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
      <Box>
        <TicketTabs
          onTabChange={() => {
            setShowSelection('');
            setAnchorEl(null);
          }}
        >
          <Box display="flex" alignItems="center">
            <Box sx={{ mx: 2 }}>
              <Button
                onClick={handleClick}
                variant="outlined"
                size="small"
                startIcon={<Iconify icon="mdi:dots-horizontal" width={24} height={24} sx={{ ml: 1 }} />}
              />
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                slotProps={{
                  list: {
                    'aria-labelledby': 'basic-button',
                  },
                  paper: {
                    sx: {
                      width: 220,
                      p: 1,
                      borderRadius: '5px',
                      boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                      '& .MuiMenu-list': {
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                      },
                    },
                  },
                }}
              >
                {isActive ? (
                  [
                    <MenuItem
                      key="complete"
                      disableRipple
                      disableGutters
                      sx={{
                        p: 0,
                        minHeight: 'unset',
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                      }}
                      onClick={() => handleClose('Mark as complete')}
                    >
                      <DropdownAction badgeIcon="tabler:checkbox">Mark as complete</DropdownAction>
                    </MenuItem>,
                    <ScopeGuard key="scope-guard" scopes={['Facility-Manager']}>
                      <MenuItem
                        disableRipple
                        disableGutters
                        sx={{
                          p: 0,
                          minHeight: 'unset',
                          '&:hover': {
                            bgcolor: 'transparent',
                          },
                        }}
                        onClick={() => markAllAsRead(true, activeTickets)}
                      >
                        <DropdownAction badgeIcon="mdi:email-outline">Mark all as unread</DropdownAction>
                      </MenuItem>
                      <MenuItem
                        disableRipple
                        disableGutters
                        sx={{
                          p: 0,
                          minHeight: 'unset',
                          '&:hover': {
                            bgcolor: 'transparent',
                          },
                        }}
                        onClick={() => markAllAsRead(false, activeTickets)}
                      >
                        <DropdownAction badgeIcon="mdi:email-open-outline">Mark all as read</DropdownAction>
                      </MenuItem>
                    </ScopeGuard>,
                  ]
                ) : (
                  <MenuItem
                    disableRipple
                    disableGutters
                    sx={{
                      p: 0,
                      minHeight: 'unset',
                      '&:hover': {
                        bgcolor: 'transparent',
                      },
                    }}
                    onClick={() => handleClose('Mark as incomplete')}
                  >
                    <DropdownAction badgeIcon="tabler:checkbox">Mark as incomplete</DropdownAction>
                  </MenuItem>
                )}
              </Menu>
            </Box>
            {!isActive && (
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                  <DateRangePickerSelect
                    pickerSx={{
                      height: '30px',
                      '& .MuiOutlinedInput-root': { height: '30px', minHeight: '30px' },
                    }}
                    buttonSx={{ mt: 0 }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </TicketTabs>
      </Box>

      {showSelection && (
        <Grid item xs={12} sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            sx={{ mr: 1, mb: 1 }}
            onClick={() => toggleComplete(showSelection === 'Mark as complete')}
          >
            {showSelection}
          </Button>
          <Button sx={{ mr: 1, mb: 1 }} onClick={handleCancel}>
            Cancel
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
